'use client';
import { Alert, Box, Link, Typography, Modal, Paper, IconButton, Stack, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LinearProgress from '@mui/material/LinearProgress';
import { SignInPage } from '@toolpad/core/SignInPage';
import * as React from 'react';
import { Navigate, useNavigate, useLocation, Link as RouterLink } from 'react-router';
import { useSession, type Session } from '../SessionContext';
import { signInWithCredentials, signInWithGoogle } from '../firebase/auth';
import PrivacyPolicyTerms from './policy/PrivacyPolicyTerms';

// Define a more accurate type for auth results
export interface AuthResult {
  success?: boolean;
  user?: any | null;
  error?: string | null;
  authUser?: any;
  accountType?: string;
  dayliteAuthorized?: boolean;
}

export default function SignIn() {
  const { session, loading } = useSession(); // Session state from context
  const navigate = useNavigate();
  const location = useLocation();
  const [isProcessing, setIsProcessing] = React.useState<boolean>(false); // Local state for sign-in button press
  const [privacyModalOpen, setPrivacyModalOpen] = React.useState(false);

  const navigatedFrom = location.state?.from; // Original location before redirect to sign-in

  // Navigate after successful login (when session is available and context is not loading)
  React.useEffect(() => {
    if (!loading && session) {
      const from = navigatedFrom?.pathname || '/';
      const search = navigatedFrom?.search || '';
      // Only navigate if we are currently on the sign-in page
      if (location.pathname === '/sign-in') {
        navigate(from + search, { replace: true });
      }
    }
  }, [session, loading, navigate, navigatedFrom, location.pathname]);

  // Redirect away if user is already logged in and didn't just get redirected here
  if (session && !navigatedFrom) {
    return <Navigate to="/" />;
  }

  // Show loading indicator while SessionContext is verifying auth state
  if (loading && !isProcessing) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh'
        }}
      >
        <Box sx={{ m: 2 }}>
          <LinearProgress color="primary" sx={{ width: 200 }} variant="indeterminate" />
        </Box>
        <Box sx={{ mt: 2, typography: 'body1' }}>Getting account...</Box>
      </Box>
    );
  }

  // Privacy Policy Modal Component
  const PrivacyPolicyModal = () => (
    <Modal open={privacyModalOpen} onClose={() => setPrivacyModalOpen(false)} aria-labelledby="privacy-policy-title">
      <Paper
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90vw',
          maxWidth: 800,
          maxHeight: '90vh',
          overflow: 'auto',
          outline: 'none'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <IconButton onClick={() => setPrivacyModalOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Stack spacing={2}>
          <PrivacyPolicyTerms />
          <Box sx={{ px: 4, pb: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              size="large"
              color="primary"
              onClick={() => setPrivacyModalOpen(false)}
            >
              Close
            </Button>
          </Box>
        </Stack>
      </Paper>
    </Modal>
  );

  return (
    <>
      <Box sx={{ position: 'relative' }}>
        {/* Show progress bar only during the actual sign-in API call */}
        {isProcessing && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 9999
            }}
          >
            <LinearProgress />
          </Box>
        )}
        <SignInPage
          providers={[
            { id: 'google', name: 'Google' },
            { id: 'credentials', name: 'Credentials' }
          ]}
          slots={{
            signUpLink: () => (
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <Link component={RouterLink} to="/forgot-password">
                    Forgot your password?
                  </Link>
                </Typography>
              </Box>
            )
          }}
          signIn={async (provider, formData) => {
            setIsProcessing(true);
            let result: AuthResult = { success: false };
            try {
              if (provider.id === 'google') {
                result = (await signInWithGoogle()) as AuthResult;
              } else if (provider.id === 'credentials') {
                const email = formData?.get('email') as string;
                const password = formData?.get('password') as string;

                if (!email || !password) {
                  return { error: 'Email and password are required' };
                }
                result = (await signInWithCredentials(email, password)) as AuthResult;
              } else {
                return { error: 'Invalid provider' };
              }

              // If Firebase call failed, return error to display
              if (!result?.success) {
                return { error: result?.error || 'Failed to sign in' };
              }

              // If Firebase call succeeded, return empty object.
              // The useEffect hook handles navigation based on SessionContext update.
              return {};
            } catch (error) {
              const errorMsg = error instanceof Error ? error.message : 'An error occurred';
              return { error: errorMsg };
            } finally {
              // Ensure local processing indicator stops
              setIsProcessing(false);
            }
          }}
        />
      </Box>

      {/* Privacy Policy Modal */}
      <PrivacyPolicyModal />
    </>
  );
}
